/*
 * Governance Tile · Header row.
 *
 * Ports the mockup v8.8.12 header block into React:
 *   · H1 heading with cyan accent on the word "team"
 *   · Two zero-count compliance counters beside the H1
 *   · Session preface paragraph
 *   · Four-view toggle (Session · Weekly · Monthly · Yearly)
 *     with the always-on persistence note
 *   · Three Aggregated View placeholder panes (Weekly / Monthly /
 *     Yearly) that show when the corresponding toggle is active
 *
 * All strings imported from ./copy/header.js — every one carries the
 * [COPY-APPROVED: governance-tile-v8] operator sign-off (2026-07-24).
 *
 * IJ-081 · Phase 1 of the v4-to-v8.8.12 re-cut.
 */
import {
  H1_TITLE,
  H1_TITLE_ACCENT,
  H1_SESSION_SUFFIX,
  SESSION_PREFACE,
  ZERO_COUNTER_1_LABEL,
  ZERO_COUNTER_2_LABEL,
  VIEW_TOGGLE_ARIA,
  VIEW_BUTTON_SESSION,
  VIEW_BUTTON_WEEKLY,
  VIEW_BUTTON_MONTHLY,
  VIEW_BUTTON_YEARLY,
  VIEW_TOGGLE_NOTE,
  WEEKLY_PANE_HEADER,
  MONTHLY_PANE_HEADER,
  YEARLY_PANE_HEADER,
  WEEKLY_PANE_STUB,
  MONTHLY_PANE_STUB,
  YEARLY_PANE_STUB,
  WEEKLY_PANE_SHAPE,
  MONTHLY_PANE_SHAPE,
  YEARLY_PANE_SHAPE,
} from './copy/header';

const VIEW_BUTTONS = [
  { view: 'session', label: VIEW_BUTTON_SESSION },
  { view: 'weekly', label: VIEW_BUTTON_WEEKLY },
  { view: 'monthly', label: VIEW_BUTTON_MONTHLY },
  { view: 'yearly', label: VIEW_BUTTON_YEARLY },
];

const AGGREGATED_PANES = [
  { view: 'weekly', header: WEEKLY_PANE_HEADER, stub: WEEKLY_PANE_STUB, shape: WEEKLY_PANE_SHAPE },
  { view: 'monthly', header: MONTHLY_PANE_HEADER, stub: MONTHLY_PANE_STUB, shape: MONTHLY_PANE_SHAPE },
  { view: 'yearly', header: YEARLY_PANE_HEADER, stub: YEARLY_PANE_STUB, shape: YEARLY_PANE_SHAPE },
];

function ZeroCounter({ value, label, testId }) {
  return (
    <div
      data-testid={testId}
      className="flex gap-3 items-start rounded-lg border border-emerald-500/40 bg-slate-950/60 px-4 py-3 max-w-[280px]"
    >
      <div className="text-[32px] font-bold leading-none text-emerald-400 flex-shrink-0">
        {value}
      </div>
      <div className="text-[11px] leading-snug text-slate-300">{label}</div>
    </div>
  );
}

function ViewToggle({ view, setView }) {
  return (
    <div
      role="tablist"
      aria-label={VIEW_TOGGLE_ARIA}
      data-testid="governance-tile-view-toggle"
      className="flex items-center gap-2 flex-wrap mb-6 mt-1 rounded-lg border border-cyan-500/25 bg-slate-950/60 px-4 py-3"
    >
      {VIEW_BUTTONS.map(({ view: v, label }) => {
        const active = view === v;
        return (
          <button
            key={v}
            role="tab"
            aria-selected={active}
            data-testid={`governance-tile-view-${v}`}
            onClick={() => setView(v)}
            className={
              'px-3 py-1.5 rounded-md text-[13px] font-medium transition-all ' +
              (active
                ? 'bg-cyan-500/15 text-cyan-300 border border-cyan-400/70'
                : 'bg-transparent text-slate-400 border border-slate-600/40 hover:text-slate-100 hover:border-cyan-500/40')
            }
          >
            {label}
          </button>
        );
      })}
      <span className="text-[11px] leading-relaxed text-slate-400 flex-1 min-w-[240px] ml-3">
        {VIEW_TOGGLE_NOTE}
      </span>
    </div>
  );
}

function AggregatedPane({ pane }) {
  return (
    <div
      data-testid={`governance-tile-pane-${pane.view}`}
      className="rounded-xl border border-dashed border-slate-500/40 bg-slate-950/50 px-7 py-8 mb-6"
    >
      <div className="text-[18px] font-semibold text-cyan-400 mb-4 tracking-tight">
        {pane.header}
      </div>
      <div className="space-y-2 text-[14px] leading-relaxed">
        <p className="italic text-slate-400">{pane.stub}</p>
        <p className="text-slate-200">{pane.shape}</p>
      </div>
    </div>
  );
}

export default function Header({ view, setView, zeroCounters, todayISO }) {
  return (
    <div data-testid="governance-tile-header">
      <div className="flex items-start justify-between gap-8 flex-wrap mb-2">
        <div className="flex-1 min-w-[320px]">
          <h1 className="text-[28px] font-semibold tracking-tight text-slate-100">
            {H1_TITLE} <span className="text-cyan-400">{H1_TITLE_ACCENT}</span> {H1_SESSION_SUFFIX}
          </h1>
        </div>
        <div className="flex gap-3 flex-wrap flex-shrink-0">
          <ZeroCounter
            testId="governance-tile-zero-counter-1"
            value={zeroCounters[0]}
            label={ZERO_COUNTER_1_LABEL}
          />
          <ZeroCounter
            testId="governance-tile-zero-counter-2"
            value={zeroCounters[1]}
            label={ZERO_COUNTER_2_LABEL}
          />
        </div>
      </div>

      <p data-testid="governance-tile-session-preface" className="text-[14px] text-slate-400 mb-6">
        {todayISO} · {SESSION_PREFACE}
      </p>

      <ViewToggle view={view} setView={setView} />

      {AGGREGATED_PANES.map((pane) =>
        view === pane.view ? <AggregatedPane key={pane.view} pane={pane} /> : null,
      )}
    </div>
  );
}
